import { useCallback, useState } from 'react'
import { usePieChartPngExport } from '@jana-technology/platform-ui/charts'

import {
  RiskThresholdLevelCode,
  RiskThresholdLevelName
} from 'app/services/api/acceptanceCategories'

import {
  defaultPieChartSize,
  defaultPieChartSliceGap,
  PieChartContainer
} from './PieChartContainer'
import { PieInnerTexts } from './PieInnerTexts'
import {
  type LengthOfPipeProps,
  type RiskByCategoryPieChartAcceptanceCategory
} from './types'
import {
  buildPieChartConfig,
  formatComparisonSeriesName,
  formatPipeLengthValue,
  getRiskByCategoryItems,
  getRiskByCategoryPieConfig,
  getRiskByCategoryTheme,
  getSeriesIdSuffix,
  getUpdatedActivePieDataById,
  hasPieChartData,
  type ActivePieDataById
} from './utils'

const wisPieColoursByCode: Record<string, string> = {
  [RiskThresholdLevelCode.LowRisk]: '7fd288',
  [RiskThresholdLevelCode.MediumLowRisk]: 'b9dd97',
  [RiskThresholdLevelCode.MediumRisk]: 'fcec7d',
  [RiskThresholdLevelCode.MediumHighRisk]: 'fac08f',
  [RiskThresholdLevelCode.HighRisk]: 'e2838c'
}

const wisPieColoursByName: Record<string, string> = {
  [RiskThresholdLevelName.LowRisk]: '7fd288',
  [RiskThresholdLevelName.MediumLowRisk]: 'b9dd97',
  [RiskThresholdLevelName.MediumRisk]: 'fcec7d',
  [RiskThresholdLevelName.MediumHighRisk]: 'fac08f',
  [RiskThresholdLevelName.HighRisk]: 'e2838c'
}

const getWisPieColour = (
  category?: RiskByCategoryPieChartAcceptanceCategory
) => {
  if (category == null) {
    return undefined
  }

  return (
    wisPieColoursByCode[category.code] ??
    wisPieColoursByName[category.name] ??
    category.colour
  )
}

const getRiskByCategoryColorRange = (
  series: ReturnType<typeof getRiskByCategoryPieConfig>['series'],
  acceptanceCategoriesByChartItem: ReturnType<
    typeof getRiskByCategoryPieConfig
  >['acceptanceCategoriesByChartItem']
) =>
  series.flatMap((seriesItem, seriesIndex) =>
    seriesItem.values.flatMap((value, index) =>
      value
        ? [
            seriesIndex === 1
              ? getWisPieColour(acceptanceCategoriesByChartItem[index])
              : acceptanceCategoriesByChartItem[index]?.colour
          ]
        : []
    )
  )

type LengthOfPipeLegendClickData = Parameters<
  NonNullable<LengthOfPipeProps['onLegendItemClick']>
>[0]

const getAdjustedPipeLength = (
  totalPipeLength: number | undefined,
  { activeSliceCount, visiblePie }: LengthOfPipeLegendClickData
) => {
  if (totalPipeLength == null) {
    return undefined
  }

  if (activeSliceCount === visiblePie.data.length) {
    return totalPipeLength
  }

  const dataTotal = visiblePie.data.reduce(
    (total, datum) => total + datum.value,
    0
  )
  const activeDataTotal = visiblePie.activeData?.reduce(
    (total, datum) => total + datum.value,
    0
  )

  return totalPipeLength - (dataTotal - (activeDataTotal ?? 0))
}

const isTotalOrBaselineName = (name?: string) => {
  if (name == null) {
    return true
  }

  const normalizedName = name.toLowerCase()

  return (
    normalizedName.startsWith('total') || normalizedName.startsWith('baseline')
  )
}

export const LengthOfPipe = ({
  acceptanceCategories,
  categoryOrder,
  data,
  isCleared,
  onLegendItemClick,
  rangeValueType,
  unitSymbol,
  title,
  ...props
}: LengthOfPipeProps) => {
  const [adjustedLength, setAdjustedLength] = useState(data?.totalPipeLength)
  const [adjustedWisLength, setAdjustedWisLength] = useState(
    data?.totalPipeLength
  )
  const [activeDataByPieId, setActiveDataByPieId] = useState<ActivePieDataById>(
    {}
  )

  const {
    acceptanceCategoriesByChartItem,
    hasMultipleSeries,
    items: pieItems,
    series,
    tooltipVariant
  } = getRiskByCategoryPieConfig({
    acceptanceCategories: data?.acceptanceCategories ?? acceptanceCategories,
    appendRiskLabel: true,
    categoryOrder,
    chartItems: getRiskByCategoryItems(data),
    rangeValueType
  })

  const colorRange = getRiskByCategoryColorRange(
    series,
    acceptanceCategoriesByChartItem
  )

  const chartTheme = getRiskByCategoryTheme(colorRange)

  const handleLegendItemClick = useCallback(
    (legendClickData: LengthOfPipeLegendClickData) => {
      setActiveDataByPieId((currentActiveDataByPieId) =>
        getUpdatedActivePieDataById(currentActiveDataByPieId, legendClickData)
      )

      const adjustedPipeLength = getAdjustedPipeLength(
        data?.totalPipeLength,
        legendClickData
      )

      if (isTotalOrBaselineName(legendClickData.visiblePie.name)) {
        setAdjustedLength(adjustedPipeLength)
      } else {
        setAdjustedWisLength(adjustedPipeLength)
      }

      onLegendItemClick?.(legendClickData)
    },
    [data?.totalPipeLength, onLegendItemClick]
  )

  const pies = series.map((seriesItem, index) => {
    const pieId = `length-of-pipe-${getSeriesIdSuffix(index)}`

    return buildPieChartConfig({
      id: pieId,
      items: pieItems,
      name: hasMultipleSeries ? seriesItem.name : undefined,
      seriesKey: seriesItem.name,
      values: seriesItem.values,
      activeData: activeDataByPieId[pieId],
      isRiskSignificance: true,
      onLegendItemClick: handleLegendItemClick,
      tooltipVariant,
      unitSymbol
    })
  })

  const innerTexts = (
    <PieInnerTexts
      formatSeriesName={formatComparisonSeriesName}
      getValue={(seriesName) =>
        formatPipeLengthValue(
          isTotalOrBaselineName(seriesName)
            ? adjustedLength
            : adjustedWisLength,
          unitSymbol
        )
      }
      isVisible={!isCleared && pies.length > 0}
      label='Pipe Length'
      series={series}
      singleSeriesLabel='Total Pipe Length'
    />
  )

  const parentRef = usePieChartPngExport({
    colorRange: chartTheme?.colors,
    pies,
    restrictVerticalWidth: false,
    size: defaultPieChartSize,
    sliceGap: defaultPieChartSliceGap,
    theme: chartTheme,
    title,
    ...props,
    children: hasPieChartData(pies) ? innerTexts : null
  })

  return (
    <div ref={parentRef} className='h-full w-full min-w-0'>
      <PieChartContainer
        colorRange={chartTheme?.colors}
        pies={pies}
        theme={chartTheme}
        {...props}
      >
        {innerTexts}
      </PieChartContainer>
    </div>
  )
}
